// Global tuning + run-mode flags.
//
// SHOT MODE is the important thing in this file. The gauntlet loop compares the same four camera
// stations round over round; if anything in the frame is a function of wall-clock time or of frame
// rate, two captures of an UNCHANGED build differ, and that difference reads as a finding when it
// is really an instrument artifact. So `?shot=1` pins the clock and any phase-driven effect to a
// fixed t.

const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');

export const MODE = {
  /** Deterministic capture mode: frozen clock, no pointer lock, camera parked at a station. */
  shot: params.get('shot') === '1',
  /** Which fixed camera station to park at (1..N). 0 = free play. */
  cam: parseInt(params.get('cam') || '0', 10),
  /**
   * VIEW. Free play defaults to THIRD person (unification brief): the player is a full Cyborg
   * rig, and a chase camera is the only way to actually see its locomotion, proportions and
   * interactions. Capture stations stay FIRST person unless asked otherwise, so every System 1-9
   * evidence frame remains comparable with the baseline it was scored against.
   */
  view: params.get('view') || (params.get('shot') === '1' ? 'fp' : 'tp'),
  /**
   * MOBILE. Forced on/off with ?mobile=1 / ?mobile=0 so both paths are testable from one machine;
   * otherwise inferred from a coarse pointer. Drives the input layer AND the quality tier -- a
   * phone that renders the desktop budget at 8 fps is not "playable on mobile".
   */
  /**
   * STATIC POSE DEBUG (?debugPose=1&pose=1..5&cam=front|side|top). Freezes the rig in a reference
   * pose with locomotion off, under the gait pass's flat review lighting, at one of three
   * orthogonal stations. Separates "the mesh is mis-oriented" from "the gait is wrong" from "the
   * IK is clamping" -- three bugs that look identical on a moving character.
   */
  debugPose: params.get('debugPose') === '1',
  /** ?debugGait=exaggerated -- goose-step stress test in the whitebox room. */
  debugGait: params.get('debugGait') || '',
  pose: parseInt(params.get('pose') || '1', 10),
  poseCam: params.get('cam') || 'front',
  mobile: params.get('mobile') === '1' || (params.get('mobile') !== '0'
    && typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
    && params.get('shot') !== '1'),
  /** Force a specific frozen time, in seconds. */
  t: parseFloat(params.get('t') || '0'),
  /** Show collision volumes / layout debug. */
  debug: params.get('debug') === '1',
  /** Perf probe: run uncapped and report frame times. */
  perf: params.get('perf') === '1',
  /**
   * NEGATIVE CONTROL for the non-blank gate. 1 = clear to a solid colour and draw nothing.
   * 2 = draw the sky gradient only, no city.
   *
   * This exists so the gate can prove its own blank-check FIRES. A fence that has never been seen
   * to fail is not evidence -- the project rule is "name the way this reports a pass on a broken
   * build", and the answer has to be demonstrated, not argued.
   */
  blank: parseInt(params.get('blank') || '0', 10),
  /**
   * GAIT REVIEW PASS. Neutral fill, rain off, static camera, subject filling the frame.
   *
   * A critic refused to score the first contact sheet and was right to: the camera was inside a
   * wall, and even framed correctly a walk cycle shot in a dark rainy alley is unjudgeable because
   * the feet live in the darkest region of the frame. "Gait review and beauty review are different
   * passes." This is the gait pass.
   */
  gait: params.get('gait') === '1',
  /** Which creature the contact-sheet camera (cam 6) tracks: '' = fauna, 'robot', 'cyborg'. */
  track: params.get('track') || '',
};

/** In shot mode the whole sim advances to this time and stops. */
export const FROZEN_T = MODE.t || 12.0;

export const WORLD = {
  // --- Main street -------------------------------------------------------
  // Roadway is 10 m kerb-to-kerb, sidewalks 3 m each side => 16 m building face to building face.
  // That is a real dense-downtown cross section; wider reads as American suburb, narrower reads as
  // a European old town. This street should feel like Kowloon or Shinjuku's back blocks.
  ROAD_HALF: 5.0,
  SIDEWALK: 3.0,
  get FACE() { return this.ROAD_HALF + this.SIDEWALK; }, // 8.0 -- x of the building faces
  STREET_Z0: -42,
  STREET_Z1: 42,
  KERB_H: 0.15,
  ROAD_CROWN: 0.06,   // camber: centreline sits this much above the gutter

  // --- Block depth -------------------------------------------------------
  BLOCK_DEPTH: 20,    // building footprints run from FACE to FACE+BLOCK_DEPTH

  // --- Player ------------------------------------------------------------
  EYE_H: 1.66,
  PLAYER_R: 0.30,
  WALK_SPEED: 1.55,   // m/s. Deliberately slow -- this is a walking-pace game, not an FPS.
  // Third-person chase camera (unification brief): behind, above, and slightly off the shoulder.
  TP_BACK: 2.8, TP_UP: 0.40, TP_SIDE: 0.32, TP_FOCUS: 1.30,
  RUN_SPEED: 3.4,
  STEP_UP: 0.36,
  GRAVITY: 18.0,

  // --- Render ------------------------------------------------------------
  FOG_NEAR: 4,
  FOG_FAR: 85,
  FOV: 62,            // ~28 mm full-frame equivalent; phone-camera-ish, matches the brief's target
  NEAR: 0.08,
  FAR: 400,
};

/** Elevated walkway height above street. */
export const WALKWAY_Y = 6.4;

/**
 * Covered arcade ceiling height.
 *
 * Round 5: 4.6 -> 5.4. A critic measured the built result at ~3.4 m clear to the beam soffit over a
 * ~4.5 m width and called it "a corridor, not an arcade" -- narrower than it is tall, which no real
 * arcade is. A covered shopping street is 5.5-7.0 m wide and 4.5-6.0 m to the soffit. Width is set
 * in layout.js ARCADE.
 */
export const ARCADE_Y = 5.4;
